import clsx from 'clsx';

import { useLocalize } from '../../hooks';
import { MoreActions } from '../../components';
import type { CompleteQuizResult } from '../../api';

type ReviewProps = {
  result?: CompleteQuizResult;

  goToCourse?: () => void;
  goToSummary?: () => void;
};

const Flag = () => {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.26 7.31333C17.0378 7.12833 16.7768 6.99572 16.4963 6.92532C16.2159 6.85493 15.9232 6.84855 15.64 6.90667C15.319 6.96713 14.9932 6.99837 14.6666 7C13.8462 6.99827 13.0385 6.79692 12.3133 6.41333C11.3966 5.92293 10.373 5.66646 9.33332 5.66667C8.65332 5.6659 7.97775 5.77624 7.33333 5.99333V5.66667C7.33333 5.48986 7.26309 5.32029 7.13807 5.19526C7.01304 5.07024 6.84348 5 6.66666 5C6.48985 5 6.32029 5.07024 6.19526 5.19526C6.07024 5.32029 6 5.48986 6 5.66667V20.3333C6 20.5101 6.07024 20.6797 6.19526 20.8047C6.32029 20.9298 6.48985 21 6.66666 21C6.84348 21 7.01304 20.9298 7.13807 20.8047C7.26309 20.6797 7.33333 20.5101 7.33333 20.3333V14.0867C8.02441 13.7838 8.77514 13.6415 9.52912 13.6703C10.2831 13.6992 11.0207 13.8985 11.6866 14.2533C12.4405 14.6556 13.2678 14.9014 14.1191 14.9759C14.9703 15.0503 15.8277 14.952 16.64 14.6867C17.038 14.5538 17.3839 14.2985 17.6281 13.9572C17.8724 13.616 18.0025 13.2063 18 12.7867V8.87333C18.0001 8.57468 17.9338 8.27974 17.8058 8.0099C17.6778 7.74007 17.4914 7.50212 17.26 7.31333Z"
        fill="currentColor"
      />
    </svg>
  );
};

export const Review: React.FC<ReviewProps> = ({
  result,
  goToCourse,
  goToSummary,
}) => {
  const { t } = useLocalize();
  if (!result?.answers?.length) return '';

  const options = [];
  goToCourse &&
    options.push({ label: t('course.page.backtocourse'), onClick: goToCourse });
  goToSummary &&
    options.push({
      label: t('course.page.backtosummary'),
      onClick: goToSummary,
    });

  return (
    <div className="flex flex-col gap-y-list mt-md pb-lg relative">
      {!!options.length && (
        <div className="absolute right-0 top-0">
          <MoreActions dock="right" options={options} />
        </div>
      )}
      {result.answers.map((answer, index) => {
        return (
          <div key={index} className="flex flex-col">
            <div
              className={clsx(
                'shrink-0 flex flex-row items-center pr-xl',
                answer.correct ? 'text-success' : 'text-danger'
              )}
            >
              <Flag />
              <div className="text-medium font-bold text-darkText">
                {t('quiz.page.question')}: {index + 1}
              </div>
              <div className="ml-sm text-medium font-bold">
                {answer.correct
                  ? t('quiz.page.correct')
                  : t('quiz.page.incorrect')}
              </div>
            </div>
            <div className="text-small font-medium mt-sm">
              {answer.question}
            </div>
            <div
              className={clsx(
                'mt-md text-small p-md rounded-bt border border-solid',
                answer.correct
                  ? 'bg-correctBack border-completed'
                  : 'bg-nonCorrectBack border-nonCorrect'
              )}
            >
              <span className="font-medium mr-xs">
                {t('quiz.page.answer')}:
              </span>
              {answer.answer}
            </div>
          </div>
        );
      })}
    </div>
  );
};
